import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Users, Search, Plus, Phone, Mail, Building2, MapPin, Edit2, Trash2, 
  MessageSquare, Bell, TrendingUp, Calendar, Clock, CheckCircle2, 
  AlertCircle, BarChart3, User, FileText, Euro, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR');
  } catch {
    return dateStr;
  }
}

// Stats Cards Component
function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Total Clients</CardDescription>
          <CardTitle className="text-2xl">{stats.nb_clients || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 text-xs text-muted-foreground">
            <span>{stats.nb_particuliers || 0} particuliers</span>
            <span>·</span>
            <span>{stats.nb_entreprises || 0} entreprises</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>CA Total Clients</CardDescription>
          <CardTitle className="text-2xl text-emerald-600">{formatCurrency(stats.ca_total)}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">Chiffre d&apos;affaires généré</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Relances en attente</CardDescription>
          <CardTitle className="text-2xl text-amber-600">{stats.relances_pending || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">{stats.nb_relances_today || 0} pour aujourd&apos;hui</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Top Client</CardDescription>
          <CardTitle className="text-lg truncate">
            {stats.top_clients?.[0]?.nom || '-'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-emerald-600 font-medium">
            {stats.top_clients?.[0] ? formatCurrency(stats.top_clients[0].ca_total) : '-'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Client Card Component
function ClientCard({ client, onEdit, onView, onDelete }) {
  var badgeVariant = client.type_client === 'entreprise' ? 'default' : client.type_client === 'fournisseur' ? 'outline' : 'secondary';
  var badgeClass = client.type_client === 'fournisseur' ? 'text-xs border-orange-300 text-orange-600' : 'text-xs';
  var typeLabel = client.type_client === 'entreprise' ? 'Entreprise' : client.type_client === 'fournisseur' ? 'Fournisseur' : 'Particulier';
  var avatarColor = client.type_client === 'fournisseur' ? 'from-orange-500 to-amber-600' : 'from-blue-500 to-indigo-600';
  
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={function() { onView(client); }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={'h-10 w-10 rounded-full bg-gradient-to-br ' + avatarColor + ' flex items-center justify-center text-white font-medium'}>
              {(client.nom || 'C').charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-sm">{client.nom}</h3>
              <p className="text-xs text-muted-foreground">{client.email || 'Pas d\'email'}</p>
            </div>
          </div>
          <Badge variant={badgeVariant} className={badgeClass}>
            {typeLabel}
          </Badge>
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Euro className="h-3.5 w-3.5" />
            <span className="font-medium text-emerald-600">{formatCurrency(client.ca_total)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            <span>{client.nb_transactions || 0} transactions</span>
          </div>
          {client.telephone && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Phone className="h-3.5 w-3.5" />
              <span>{client.telephone}</span>
            </div>
          )}
          {client.nb_relances_pending > 0 && (
            <div className="flex items-center gap-1.5 text-amber-600">
              <Bell className="h-3.5 w-3.5" />
              <span>{client.nb_relances_pending} relance(s)</span>
            </div>
          )}
        </div>
        
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            Dernière transaction: {formatDate(client.derniere_transaction)}
          </span>
          <div className="flex gap-1" onClick={function(e) { e.stopPropagation(); }}>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={function() { onEdit(client); }}>
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={function() { onDelete(client); }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Client Detail Modal
function ClientDetailModal({ client, isOpen, onClose, onRefresh, token }) {
  var [activeTab, setActiveTab] = useState('infos');
  var [newNote, setNewNote] = useState('');
  var [noteType, setNoteType] = useState('note');
  var [savingNote, setSavingNote] = useState(false);
  var [newRelance, setNewRelance] = useState({ titre: '', description: '', date_relance: '', priorite: 'normale' });
  var [savingRelance, setSavingRelance] = useState(false);

  async function handleAddNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(API_URL + '/api/crm/notes', {
        client_id: client.id,
        contenu: newNote,
        type_note: noteType
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Note ajoutée');
      setNewNote('');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setSavingNote(false);
    }
  }

  async function handleDeleteNote(noteId) {
    try {
      await axios.delete(API_URL + '/api/crm/notes/' + noteId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Note supprimée');
      onRefresh();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  async function handleAddRelance() {
    if (!newRelance.titre || !newRelance.date_relance) {
      toast.error('Titre et date requis');
      return;
    }
    setSavingRelance(true);
    try {
      await axios.post(API_URL + '/api/crm/relances', {
        client_id: client.id,
        ...newRelance
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Relance créée');
      setNewRelance({ titre: '', description: '', date_relance: '', priorite: 'normale' });
      onRefresh();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSavingRelance(false);
    }
  }

  async function handleUpdateRelance(relanceId, statut) {
    try {
      await axios.put(API_URL + '/api/crm/relances/' + relanceId, { statut: statut }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Relance mise à jour');
      onRefresh();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  if (!client) return null;

  var noteTypeIcons = {
    note: MessageSquare,
    appel: Phone,
    email: Mail,
    reunion: Calendar
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-medium">
              {(client.nom || 'C').charAt(0).toUpperCase()}
            </div>
            <div>
              <DialogTitle className="text-xl">{client.nom}</DialogTitle>
              <DialogDescription>
                {client.type_client === 'entreprise' ? 'Entreprise' : 'Particulier'} · CA: {formatCurrency(client.ca_total)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="infos">Informations</TabsTrigger>
            <TabsTrigger value="historique">Historique</TabsTrigger>
            <TabsTrigger value="notes">Notes ({client.notes?.length || 0})</TabsTrigger>
            <TabsTrigger value="relances">Relances</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            <TabsContent value="infos" className="m-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{client.email || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.telephone || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{client.adresse ? (client.adresse + ', ' + (client.code_postal || '') + ' ' + (client.ville || '')) : 'Non renseigné'}</span>
                  </div>
                  {client.siret && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>SIRET: {client.siret}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">Chiffre d&apos;affaires total</p>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(client.ca_total)}</p>
                    </CardContent>
                  </Card>
                  <div className="grid grid-cols-2 gap-2">
                    <Card>
                      <CardContent className="p-3">
                        <p className="text-xs text-muted-foreground">Transactions</p>
                        <p className="text-lg font-semibold">{client.nb_transactions || 0}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3">
                        <p className="text-xs text-muted-foreground">Fréquence</p>
                        <p className="text-lg font-semibold">
                          {client.frequence_achat_jours ? client.frequence_achat_jours + 'j' : '-'}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="historique" className="m-0">
              <div className="space-y-2">
                {client.historique && client.historique.length > 0 ? (
                  client.historique.map(function(transaction, idx) {
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{transaction.nature}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(transaction.date)} · {transaction.reference}</p>
                        </div>
                        <span className="font-semibold text-emerald-600">{formatCurrency(transaction.montant)}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Aucune transaction enregistrée</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="notes" className="m-0">
              <div className="space-y-4">
                {/* Add Note Form */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex gap-2 mb-2">
                      <Select value={noteType} onValueChange={setNoteType}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="note">Note</SelectItem>
                          <SelectItem value="appel">Appel</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="reunion">Réunion</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        placeholder="Ajouter une note..." 
                        value={newNote} 
                        onChange={function(e) { setNewNote(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddNote(); }}
                        className="flex-1"
                      />
                      <Button onClick={handleAddNote} disabled={savingNote}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes List */}
                <div className="space-y-2">
                  {client.notes && client.notes.length > 0 ? (
                    client.notes.map(function(note) {
                      var NoteIcon = noteTypeIcons[note.type_note] || MessageSquare;
                      return (
                        <div key={note.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg group">
                          <NoteIcon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                          <div className="flex-1">
                            <p className="text-sm">{note.contenu}</p>
                            <p className="text-xs text-muted-foreground mt-1">{formatDate(note.created_at)}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={function() { handleDeleteNote(note.id); }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>Aucune note</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="relances" className="m-0">
              <div className="space-y-4">
                {/* Add Relance Form */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        placeholder="Titre de la relance" 
                        value={newRelance.titre} 
                        onChange={function(e) { setNewRelance({...newRelance, titre: e.target.value}); }}
                      />
                      <Input 
                        type="date"
                        value={newRelance.date_relance} 
                        onChange={function(e) { setNewRelance({...newRelance, date_relance: e.target.value}); }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Description (optionnel)" 
                        value={newRelance.description} 
                        onChange={function(e) { setNewRelance({...newRelance, description: e.target.value}); }}
                        className="flex-1"
                      />
                      <Select value={newRelance.priorite} onValueChange={function(v) { setNewRelance({...newRelance, priorite: v}); }}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basse">Basse</SelectItem>
                          <SelectItem value="normale">Normale</SelectItem>
                          <SelectItem value="haute">Haute</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddRelance} disabled={savingRelance}>
                        <Plus className="h-4 w-4 mr-1" /> Créer
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Relances List */}
                <div className="space-y-2">
                  {client.relances && client.relances.length > 0 ? (
                    client.relances.map(function(relance) {
                      var prioriteColors = {
                        basse: 'bg-slate-100 text-slate-600',
                        normale: 'bg-blue-100 text-blue-600',
                        haute: 'bg-red-100 text-red-600'
                      };
                      var isOverdue = new Date(relance.date_relance) < new Date() && relance.statut === 'a_faire';
                      
                      return (
                        <div key={relance.id} className={'p-3 rounded-lg border ' + (isOverdue ? 'border-red-200 bg-red-50' : 'bg-slate-50')}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{relance.titre}</p>
                                <Badge className={prioriteColors[relance.priorite] + ' text-xs'}>
                                  {relance.priorite}
                                </Badge>
                              </div>
                              {relance.description && (
                                <p className="text-xs text-muted-foreground mt-1">{relance.description}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                {formatDate(relance.date_relance)}
                                {isOverdue && <span className="text-red-600 ml-2">En retard</span>}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              {relance.statut === 'a_faire' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={function() { handleUpdateRelance(relance.id, 'fait'); }}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Fait
                                </Button>
                              )}
                              {relance.statut === 'fait' && (
                                <Badge className="bg-emerald-100 text-emerald-700">Terminé</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>Aucune relance programmée</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Client Form Modal
function ClientFormModal({ client, isOpen, onClose, onSave, token }) {
  var [form, setForm] = useState({
    nom: '', email: '', telephone: '', adresse: '', ville: '', code_postal: '', siret: '', type_client: 'particulier', tags: []
  });
  var [saving, setSaving] = useState(false);

  useEffect(function() {
    if (client) {
      setForm({
        nom: client.nom || '',
        email: client.email || '',
        telephone: client.telephone || '',
        adresse: client.adresse || '',
        ville: client.ville || '',
        code_postal: client.code_postal || '',
        siret: client.siret || '',
        type_client: client.type_client || 'particulier',
        tags: client.tags || []
      });
    } else {
      setForm({ nom: '', email: '', telephone: '', adresse: '', ville: '', code_postal: '', siret: '', type_client: 'particulier', tags: [] });
    }
  }, [client]);

  async function handleSubmit() {
    if (!form.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    
    setSaving(true);
    try {
      if (client) {
        await axios.put(API_URL + '/api/crm/clients/' + client.id, form, {
          headers: { Authorization: 'Bearer ' + token }
        });
        toast.success('Client mis à jour');
      } else {
        await axios.post(API_URL + '/api/crm/clients', form, {
          headers: { Authorization: 'Bearer ' + token }
        });
        toast.success('Client créé');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{client ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Nom *</Label>
            <Input value={form.nom} onChange={function(e) { setForm({...form, nom: e.target.value}); }} placeholder="Nom du client" />
          </div>
          
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={form.type_client} onValueChange={function(v) { setForm({...form, type_client: v}); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="entreprise">Entreprise</SelectItem>
                <SelectItem value="fournisseur">Fournisseur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={function(e) { setForm({...form, email: e.target.value}); }} placeholder="email@exemple.fr" />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input value={form.telephone} onChange={function(e) { setForm({...form, telephone: e.target.value}); }} placeholder="06 12 34 56 78" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Adresse</Label>
            <Input value={form.adresse} onChange={function(e) { setForm({...form, adresse: e.target.value}); }} placeholder="123 rue Example" />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Code postal</Label>
              <Input value={form.code_postal} onChange={function(e) { setForm({...form, code_postal: e.target.value}); }} placeholder="75001" />
            </div>
            <div className="space-y-2">
              <Label>Ville</Label>
              <Input value={form.ville} onChange={function(e) { setForm({...form, ville: e.target.value}); }} placeholder="Paris" />
            </div>
          </div>
          
          {form.type_client === 'entreprise' && (
            <div className="space-y-2">
              <Label>SIRET</Label>
              <Input value={form.siret} onChange={function(e) { setForm({...form, siret: e.target.value}); }} placeholder="123 456 789 00012" />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {client ? 'Enregistrer' : 'Créer le client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main CRM Page
function CRMPage() {
  var [loading, setLoading] = useState(true);
  var [stats, setStats] = useState({});
  var [clients, setClients] = useState([]);
  var [search, setSearch] = useState('');
  var [typeFilter, setTypeFilter] = useState('all');
  var [selectedClient, setSelectedClient] = useState(null);
  var [clientDetail, setClientDetail] = useState(null);
  var [showForm, setShowForm] = useState(false);
  var [editingClient, setEditingClient] = useState(null);
  var [deleteConfirm, setDeleteConfirm] = useState(null);
  var [activeTab, setActiveTab] = useState('clients');

  var token = localStorage.getItem('token');

  var fetchData = useCallback(async function() {
    try {
      var [statsRes, clientsRes] = await Promise.all([
        axios.get(API_URL + '/api/crm/stats', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API_URL + '/api/crm/clients', { headers: { Authorization: 'Bearer ' + token } })
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  async function fetchClientDetail(clientId) {
    try {
      var response = await axios.get(API_URL + '/api/crm/clients/' + clientId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setClientDetail(response.data);
    } catch (error) {
      toast.error('Erreur');
    }
  }

  function handleViewClient(client) {
    fetchClientDetail(client.id);
  }

  function handleEditClient(client) {
    setEditingClient(client);
    setShowForm(true);
  }

  async function handleDeleteClient() {
    if (!deleteConfirm) return;
    try {
      await axios.delete(API_URL + '/api/crm/clients/' + deleteConfirm.id, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Client supprimé');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  function handleRefreshDetail() {
    if (clientDetail) {
      fetchClientDetail(clientDetail.id);
    }
    fetchData();
  }

  var filteredClients = clients.filter(function(c) {
    var matchSearch = !search || 
      (c.nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase());
    var matchType = typeFilter === 'all' || c.type_client === typeFilter;
    return matchSearch && matchType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM Simplifié</h1>
          <p className="text-muted-foreground">Gérez vos relations clients</p>
        </div>
        <Button onClick={function() { setEditingClient(null); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="clients">
            <Users className="h-4 w-4 mr-2" />
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="relances">
            <Bell className="h-4 w-4 mr-2" />
            Relances ({stats.relances_pending || 0})
          </TabsTrigger>
          <TabsTrigger value="top">
            <BarChart3 className="h-4 w-4 mr-2" />
            Top Clients
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-4">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Rechercher un client..." 
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type de contact" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="particulier">Particuliers</SelectItem>
                <SelectItem value="entreprise">Entreprises</SelectItem>
                <SelectItem value="fournisseur">Fournisseurs</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clients Grid */}
          {filteredClients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClients.map(function(client) {
                return (
                  <ClientCard
                    key={client.id}
                    client={client}
                    onView={handleViewClient}
                    onEdit={handleEditClient}
                    onDelete={function() { setDeleteConfirm(client); }}
                  />
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-semibold mb-2">Aucun client</h3>
                <p className="text-muted-foreground mb-4">
                  {search ? 'Aucun résultat pour cette recherche' : 'Commencez par ajouter votre premier client'}
                </p>
                {!search && (
                  <Button onClick={function() { setEditingClient(null); setShowForm(true); }}>
                    <Plus className="h-4 w-4 mr-2" /> Ajouter un client
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="relances" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Relances à effectuer</CardTitle>
              <CardDescription>Vos rappels commerciaux en attente</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.relances_today && stats.relances_today.length > 0 ? (
                <div className="space-y-3">
                  {stats.relances_today.map(function(relance) {
                    var isOverdue = new Date(relance.date_relance) < new Date();
                    return (
                      <div key={relance.id} className={'p-4 rounded-lg border ' + (isOverdue ? 'border-red-200 bg-red-50' : 'bg-slate-50')}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{relance.titre}</p>
                            <p className="text-sm text-muted-foreground">
                              {relance.client_nom} · {formatDate(relance.date_relance)}
                              {isOverdue && <span className="text-red-600 ml-2 font-medium">En retard</span>}
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={function() {
                            var client = clients.find(function(c) { return c.id === relance.client_id; });
                            if (client) handleViewClient(client);
                          }}>
                            Voir <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p className="font-medium">Aucune relance en attente</p>
                  <p className="text-sm">Vous êtes à jour !</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="top" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Clients</CardTitle>
              <CardDescription>Vos meilleurs clients par chiffre d&apos;affaires</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.top_clients && stats.top_clients.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_clients.map(function(client, idx) {
                    return (
                      <div key={client.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{client.nom}</p>
                          <p className="text-xs text-muted-foreground">{client.nb_transactions} transactions</p>
                        </div>
                        <p className="font-semibold text-emerald-600">{formatCurrency(client.ca_total)}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Pas encore de données</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ClientFormModal
        client={editingClient}
        isOpen={showForm}
        onClose={function() { setShowForm(false); setEditingClient(null); }}
        onSave={fetchData}
        token={token}
      />

      <ClientDetailModal
        client={clientDetail}
        isOpen={!!clientDetail}
        onClose={function() { setClientDetail(null); }}
        onRefresh={handleRefreshDetail}
        token={token}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={function() { setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les notes et relances associées à {deleteConfirm?.nom} seront également supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CRMPage;
